import { useState } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';
import { safeCastPlan } from '@/types/user';
import { useRealTimeUpdates } from './useRealTimeUpdates';

interface GenerateOptions {
  prompt: string;
  type: 'image' | 'voice' | 'script';
  style?: string;
  resolution?: string;
  voice?: string;
  language?: string;
  scriptType?: string;
  duration?: number;
  images?: string[];
  realistic?: boolean;
  uploadedImage?: string | null;
}

export const useGenovaAI = () => {
  const { user, updateUser } = useAuth();
  const [isGenerating, setIsGenerating] = useState(false);
  
  const { syncUserData } = useRealTimeUpdates({
    onProfileUpdate: (payload) => {
      console.log('Profile updated in real-time:', payload);
    },
    onContentUpdate: (payload) => {
      console.log('Content updated in real-time:', payload);
    }
  });

  const syncCreditsWithDatabase = async () => {
    if (!user?.id) return;

    try {
      // Force monthly reset check - this will reset if we're in a new month
      const { error: resetError } = await supabase.rpc('ensure_monthly_usage_reset', { p_user_id: user.id });
      
      if (resetError) {
        console.error('Monthly reset check failed:', resetError);
      }
      
      // Now fetch the updated profile data
      const { data, error } = await supabase
        .from('profiles')
        .select('monthly_images_used, monthly_scripts_used, monthly_audio_minutes_used, images_limit, scripts_limit, audio_minutes_limit, plan, last_usage_reset')
        .eq('user_id', user.id)
        .maybeSingle();

      if (error && error.code === 'PGRST116') {
        // Create profile if it doesn't exist with proper free plan limits
        const { error: insertError } = await supabase
          .from('profiles')
          .insert({
            user_id: user.id,
            monthly_images_used: 0,
            monthly_scripts_used: 0,
            monthly_audio_minutes_used: 0,
            images_limit: 5,
            scripts_limit: 5,
            audio_minutes_limit: 0.33,
            plan: 'free',
            full_name: user.email?.split('@')[0] || 'User',
            last_usage_reset: new Date().toISOString()
          });
        
        if (insertError) {
          console.error('Error creating profile:', insertError);
          return;
        }
        
        updateUser({
          plan: 'free' as any,
          monthly_images_used: 0,
          monthly_scripts_used: 0,
          monthly_audio_minutes_used: 0,
          images_limit: 5,
          scripts_limit: 5,
          audio_minutes_limit: 0.33
        });
        return;
      }

      if (error) {
        console.error('Error syncing usage:', error);
        return;
      }

      if (data) {
        console.log('Synced credits - Last reset:', data.last_usage_reset, 'Usage:', {
          images: data.monthly_images_used,
          scripts: data.monthly_scripts_used,
          audio: data.monthly_audio_minutes_used
        });
        
        updateUser({
          plan: safeCastPlan(data.plan),
          monthly_images_used: data.monthly_images_used || 0,
          monthly_scripts_used: data.monthly_scripts_used || 0,
          monthly_audio_minutes_used: data.monthly_audio_minutes_used || 0,
          images_limit: data.images_limit || 5,
          scripts_limit: data.scripts_limit || 5,
          audio_minutes_limit: data.audio_minutes_limit || 0.33
        });
      }
    } catch (error) {
      console.error('Error in syncCreditsWithDatabase:', error);
    }
  };

  const checkUsageLimits = async (type: 'image' | 'voice' | 'script', duration?: number) => {
    if (!user?.id) {
      toast.error('Please log in to generate content', {
        description: 'You need to be signed in to use AI generation features'
      });
      return false;
    }

    try {
      await syncCreditsWithDatabase();

      const { data, error } = await supabase
        .from('profiles')
        .select(`
          monthly_images_used, monthly_scripts_used, monthly_audio_minutes_used,
          images_limit, scripts_limit, audio_minutes_limit, plan
        `)
        .eq('user_id', user.id)
        .maybeSingle();

      if (error) {
        toast.error('Unable to verify usage limits. Please try again.', {
          description: 'There was an issue checking your account status'
        });
        return false;
      }

      const profile = data || {
        monthly_images_used: 0,
        images_limit: 5,
        monthly_scripts_used: 0,
        scripts_limit: 5,
        monthly_audio_minutes_used: 0,
        audio_minutes_limit: 0.33,
        plan: 'free'
      };
      
      const plan = profile.plan ?? 'free';

      // Check voice duration limit for free users (20 seconds max)
      if (type === 'voice' && plan === 'free' && duration && duration > 20) {
        toast.error('Free users limited to 20 second voice generations', {
          description: 'Upgrade to Premium for longer audio (up to 5 minutes)!',
          action: {
            label: 'Upgrade Now',
            onClick: () => window.location.href = '/pricing',
          },
        });
        return false;
      }

      let used = 0;
      let limit = 0;
      let limitType = '';

      // Global free plan cap: 5 total generations across images, scripts, and audio minutes
      if (plan === 'free') {
        const totalUsed = (profile.monthly_images_used || 0) + (profile.monthly_scripts_used || 0) + (profile.monthly_audio_minutes_used || 0);
        if (totalUsed >= 5) {
          toast.error(`You've reached your free plan limit (5 total generations)`, {
            description: 'Upgrade your plan for higher limits!',
            action: {
              label: 'Upgrade Now',
              onClick: () => window.location.href = '/pricing',
            },
          });
          return false;
        }
      }

      switch (type) {
        case 'image':
          used = profile.monthly_images_used || 0;
          limit = profile.images_limit ?? 5;
          limitType = 'images';
          break;
        case 'script':
          used = profile.monthly_scripts_used || 0;
          limit = profile.scripts_limit ?? 5;
          limitType = 'scripts';
          break;
        case 'voice':
          used = profile.monthly_audio_minutes_used || 0;
          limit = profile.audio_minutes_limit ?? 0.33; // 20 seconds for free
          const limitSeconds = Math.round(limit * 60);
          const usedSeconds = Math.round(used * 60);
          limitType = `audio (${usedSeconds}/${limitSeconds} seconds)`;
          break;
      }

      if (used >= limit) {
        const displayLimit = type === 'voice' ? `${Math.round(limit * 60)} seconds` : limit.toString();
        toast.error(`You've reached your ${limitType} limit (${displayLimit})`, {
          description: 'Upgrade your plan for higher limits!',
          action: {
            label: 'Upgrade Now',
            onClick: () => window.location.href = '/pricing',
          },
        });
        return false;
      }

      return true;
    } catch (error) {
      console.error('Error in checkUsageLimits:', error);
      toast.error('Unable to verify usage limits. Please try again.', {
        description: 'There was an unexpected error'
      });
      return false;
    }
  };

  const generateContent = async (options: GenerateOptions) => {
    // Pass duration for voice generation limits
    const canGenerate = await checkUsageLimits(options.type, options.duration);
    if (!canGenerate) return null;

    setIsGenerating(true);
    
    try {
      const functionMap = {
        image: 'generate-image',
        voice: 'generate-voice',
        script: 'generate-script'
      };

      toast.info(`🎨 Generating ${options.type}...`, {
        description: 'This may take a few moments',
        duration: 4000
      });

      console.log('Calling function:', functionMap[options.type], 'with options:', options);

      // Build payload per type to match Edge Function contracts
      const payload = options.type === 'script'
        ? { topic: options.prompt, scriptType: options.scriptType || 'script' }
        : options.type === 'voice'
          ? { text: options.prompt, voice: options.voice || 'alloy', language: options.language || 'en', duration: options.duration }
          : { prompt: options.prompt, style: options.style || 'photographic', resolution: options.resolution || '1024x1024', realistic: options.realistic || false, uploadedImage: options.uploadedImage || null };

      const { data, error } = await supabase.functions.invoke(functionMap[options.type], {
        body: { 
          ...payload,
          userId: user?.id
        }
      });

      console.log('Function response:', { data, error });

      if (error) {
        console.error('Function invocation error:', error);
        throw new Error(error.message || `${options.type} generation failed`);
      }

      if (!data) {
        throw new Error(`No response from ${options.type} generation service`);
      }

      if (!data.success) {
        throw new Error(data.error || `${options.type} generation failed`);
      }

      // Sync user data after successful generation
      if (user?.id) {
        await syncUserData();
      }
      
      toast.success(`✨ ${options.type.charAt(0).toUpperCase() + options.type.slice(1)} generated successfully!`, {
        description: 'Your content is ready to use'
      });
      
      return data;

    } catch (error) {
      console.error('Content generation error:', error);
      const errorMessage = error instanceof Error ? error.message : `${options.type} generation failed`;
      toast.error(errorMessage, {
        description: 'Please try again or contact support if the issue persists'
      });
      return null;
    } finally {
      setIsGenerating(false);
    }
  };

  const getUserContent = async (type?: string) => {
    if (!user?.id) return [];

    try {
      let query = supabase
        .from('generated_content')
        .select('*')
        .eq('user_id', user.id)
        .order('created_at', { ascending: false })
        .limit(50);

      if (type) {
        query = query.eq('type', type);
      }

      const { data, error } = await query;

      if (error) {
        console.error('Error fetching content:', error);
        return [];
      }

      return data || [];
    } catch (error) {
      console.error('Error in getUserContent:', error);
      return [];
    }
  };

  const getUserScripts = async () => {
    if (!user?.id) return [];

    try {
      const { data, error } = await supabase
        .from('script_generations')
        .select('*')
        .eq('user_id', user.id)
        .order('created_at', { ascending: false })
        .limit(20);

      if (error) {
        console.error('Error fetching scripts:', error);
        return [];
      }

      return data || [];
    } catch (error) {
      console.error('Error in getUserScripts:', error);
      return [];
    }
  };

  return {
    isGenerating,
    generateContent,
    getUserContent,
    getUserScripts,
    checkUsageLimits,
    syncCreditsWithDatabase,
    syncUserData
  };
};
